import React, { useState, useContext, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  InputGroup,
  Table
} from 'react-bootstrap';
import AppContext from 'context/Context';
import { RESPONSE_CODE } from 'types/const';
import { USE_YN_SELECT } from 'types/type';

// api
import { inspIrdntList } from 'api/haccp/inspStandardInfo/ingredientsMgmtApi';
import { commonCode } from 'api/haccp/commonApi';

const InspectionGroupModal = props => {
  const {
    config: { isDark }
  } = useContext(AppContext);

  /* props */
  const { modalShow, setModalShow, modalConfirm, itemList, setItemList } =
    props || {};

  /* state */
  const [currentPage, setCurrentPage] = useState(1);
  const [agencyOptions, setAgencyOptions] = useState(null);
  const [ingredientOptions, setIngredientOptions] = useState(null);
  const [isAllCheck, setIsAllCheck] = useState(false);
  const [checkCount, setCheckCount] = useState(0);

  const [search, setSearch] = useState({
    inspLabCd: null, // 의뢰기관 코드
    inspIrdntKindCd: null, // 검사성분유형 코드
    cmplxInspIrdntYn: null, // 복합검사성분여부
    divisionKey: null, // 구분
    divisionValue: null, // 구분입력값
    size: 9999,
    sort: 'seq,asc'
  });

  // 검사성분 목록
  const [list, setList] = useState([]);

  /* event */
  const handleClose = () => {
    setModalShow(!modalShow);
  };

  // Check Box  -> All Check
  const onAllCheck = e => {
    const { checked } = e.target || {};

    setIsAllCheck(checked);

    // 검사성분 API호출 Data
    setList(
      list?.map(e => {
        e.isCheck = checked;
        return e;
      })
    );

    // 기존선택한 검사성분 Data
    setItemList(
      itemList?.map(e => {
        e.isCheck = checked;
        return e;
      })
    );

    if (checked === true) {
      setCheckCount(list.length);
    } else {
      setCheckCount(0);
    }
  };

  // Check Box  -> Check
  const onCheck = (e, index) => {
    const { checked } = e.target || {};
    const newList = [...list];
    newList[index] = { ...newList[index], isCheck: checked };
    setList(newList);

    // 이미선택한 복합검사성분여부 체크
    const mappingSeq = newList[index]?.mappingSeq;
    setItemList(
      itemList?.map(e => {
        if (e.mappingSeq === mappingSeq) {
          e.isCheck = checked;
        }
        return e;
      })
    );

    // 개별 checkBox 선택 시 All Check 여부
    const check = newList?.some(e => e.isCheck === false);
    if (check === false) {
      setIsAllCheck(true);
    } else {
      setIsAllCheck(false);
    }

    // 선택한 검사항목 수
    if (checked === true) {
      setCheckCount(checkCount + 1);
    } else {
      setCheckCount(checkCount - 1);
    }
  };

  // 검사성분 선택
  const handelConfirm = () => {
    const check = list?.some(e => e.isCheck === true);
    if (check === false && checkCount === 0) return;

    let checkItems = [];

    // 검사성분 선택이 없는 경우
    if (itemList.length === 0) {
      checkItems = list?.filter(e => e.isCheck === true);
      modalConfirm(checkItems);
      return;
    }

    checkItems = list?.filter(e => e.isCheck === true);

    modalConfirm(checkItems);
  };

  // 조회
  const onSearch = async () => {
    // 초기화
    setList([]);

    const {
      inspLabCd,
      inspIrdntKindCd,
      cmplxInspIrdntYn,
      divisionKey,
      divisionValue,
      size,
      sort
    } = search || {};

    const params = {
      inspLabCd: inspLabCd,
      inspIrdntKindCd: inspIrdntKindCd,
      cmplxInspIrdntYn: cmplxInspIrdntYn,
      [divisionKey]: divisionValue,
      page: currentPage - 1,
      size: size,
      sort: sort
    };

    // request Data null key 제거
    Object.keys(params).forEach(
      key =>
        (params[key] === null ||
          params[key] === '' ||
          params[key] === 'TOTAL') &&
        delete params[key]
    );

    const response = await inspIrdntList(params);

    const { code, list } = response || {};

    if (code !== RESPONSE_CODE.Success) return;

    if (list.length === 0) {
      setList([]);
      return;
    }

    const newList = list
      ?.map(e => {
        e.isCheck = false;
        e.mappingSeq = e.seq;
        itemList?.map(item => {
          if (e.seq === item.inspIrdntSeq) {
            e.isCheck = true;
          }
        });
        return e;
      })
      ?.sort((a, b) => Number(b.isCheck) - Number(a.isCheck));

    const check = newList?.some(e => e.isCheck === false);
    if (check === false) {
      setIsAllCheck(true);
    } else {
      setIsAllCheck(false);
    }

    setCheckCount(newList?.filter(e => e.isCheck === true)?.length ?? 0);
    setList(prevState => {
      return [...prevState, ...newList];
    });
  };

  // 의뢰기관목록 API
  const requestAgency = async () => {
    const params = {
      codeGroup: 'sfsh_insp_lab',
      useYn: 'Y',
      sort: 'orders,asc'
    };
    const response = await commonCode(params);

    const { code, list } = response || {};
    if (code !== RESPONSE_CODE.Success) return;
    setAgencyOptions(list);
  };

  // 검사성분유형 API
  const ingredientList = async () => {
    const params = {
      codeGroup: 'sfsh_irdnt_kind',
      useYn: 'Y',
      sort: 'orders,asc'
    };
    const response = await commonCode(params);

    const { code, list } = response || {};
    if (code !== RESPONSE_CODE.Success) return;
    setIngredientOptions(list);
  };

  /* watch */
  useEffect(() => {
    // 의뢰기관 API 호출
    requestAgency();
    // 검사성분 유형 API 호출
    ingredientList();
    // 검사성분목록 API 호출
    onSearch();
  }, []);

  return (
    <Modal
      show={modalShow}
      onHide={handleClose}
      backdrop="static"
      size="xl"
      centered
    >
      <Modal.Header
        closeButton
        closeVariant={isDark ? 'white' : undefined}
        className="px-card border-bottom-0"
      >
        <Modal.Title>검사성분 선택</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Row className="ms-0 me-0">
            {/* 의뢰기관 Select Box*/}
            <Col sm={2} className="ps-0 pe-1">
              <Form.Select
                size="sm"
                onChange={e =>
                  setSearch({ ...search, inspLabCd: e.target.value })
                }
              >
                <option hidden>의뢰기관 선택</option>
                <option value={'TOTAL'}>전체</option>
                {agencyOptions?.map(option => (
                  <option key={option.codeCd} value={option.codeCd}>
                    {option.codeNmKr}
                  </option>
                ))}
              </Form.Select>
            </Col>
            {/* 검사성분유형 Select Box*/}
            <Col sm={2} className="ps-1 pe-1">
              <Form.Select
                size="sm"
                onChange={e =>
                  setSearch({ ...search, inspIrdntKindCd: e.target.value })
                }
              >
                <option hidden>검사성분유형 선택</option>
                <option value={'TOTAL'}>전체</option>
                {ingredientOptions?.map(option => (
                  <option key={option.codeCd} value={option.codeCd}>
                    {option.codeNmKr}
                  </option>
                ))}
              </Form.Select>
            </Col>
            {/* 복합검사성분유형 Select Box*/}
            <Col sm={2} className="ps-1 pe-1">
              <Form.Select
                size="sm"
                onChange={e =>
                  setSearch({ ...search, cmplxInspIrdntYn: e.target.value })
                }
              >
                <option hidden>복합검사여부 선택</option>
                <option value={'TOTAL'}>전체</option>
                {USE_YN_SELECT.map(option => (
                  <option key={option.key} value={option.key}>
                    {option.value}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col sm={5} className="ps-1 pe-1">
              <InputGroup size="sm" className="w-100">
                <Form.Select
                  size="sm"
                  className="w-25"
                  onChange={e =>
                    setSearch({ ...search, divisionKey: e.target.value })
                  }
                >
                  <option hidden>구분선택</option>
                  <option value={'inspIrdntCd'}>검사성분코드</option>
                  <option value={'labInspIrdntCd'}>검사결과서코드</option>
                  <option value={'inspIrdntNm'}>검사성분</option>
                </Form.Select>
                <Form.Control
                  placeholder="구분선택 후 입력해주세요."
                  type="text"
                  size="sm"
                  className="w-50"
                  maxLength="20"
                  disabled={search.divisionKey === null}
                  onChange={e =>
                    setSearch({
                      ...search,
                      divisionValue: e.target.value
                    })
                  }
                />
              </InputGroup>
            </Col>
            <Col
              sm={'auto'}
              className="ps-1 pe-1 d-flex flex-row align-items-center"
            >
              <Button size="sm" onClick={() => onSearch()}>
                조회
              </Button>
            </Col>
          </Row>
          <Row className="ms-0 me-0 mt-2">
            <Col sm={12} className="ps-1 pe-1 d-flex justify-content-end">
              <p className="mb-0 ms-0 fs--1 fw-bold lh-1 text-primary">
                선택한 검사항목 수 : {checkCount} 개
              </p>
            </Col>
          </Row>
        </Form.Group>
        <div className="scrollbar max-vh-50">
          <Table bordered hover className="fs--1 text-center">
            <thead
              className="bg-200 text-700 align-middle border-300"
              style={{ position: 'sticky', top: 0 }}
            >
              <tr>
                <th className="text-nowrap">
                  <Form.Check
                    type="checkbox"
                    checked={isAllCheck}
                    onChange={e => onAllCheck(e)}
                  />
                </th>
                <th className="text-nowrap">의뢰기관</th>
                <th className="text-nowrap">검사성분 코드</th>
                <th className="text-nowrap">검사결과서 코드</th>
                <th className="text-nowrap">검사성분</th>
                <th className="text-nowrap">검사성분유형</th>
                <th className="text-nowrap">복합 검사성분 여부</th>
              </tr>
            </thead>
            <tbody>
              {list?.map(
                (
                  {
                    isCheck,
                    inspLabNm,
                    inspIrdntCd,
                    labInspIrdntCd,
                    inspIrdntNm,
                    inspIrdntKindNm,
                    cmplxInspIrdntYn
                  },
                  index
                ) => (
                  <tr
                    key={index}
                    className={isCheck === true ? 'table-primary' : ''}
                  >
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={isCheck}
                        onChange={e => onCheck(e, index)}
                      />
                    </td>
                    <td>{inspLabNm}</td>
                    <td>{inspIrdntCd}</td>
                    <td>{labInspIrdntCd}</td>
                    <td>{inspIrdntNm}</td>
                    <td>{inspIrdntKindNm}</td>
                    <td>{cmplxInspIrdntYn}</td>
                  </tr>
                )
              )}
              {list?.length < 1 && (
                <tr>
                  <td colSpan={7} className="w-auto">
                    검사성분관리 데이터가 존재하지 않습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer className="px-card border-top-0 align-items-center justify-content-center">
        <Button variant="secondary" onClick={() => handleClose()}>
          취소
        </Button>
        <Button
          className="ms-2"
          onClick={() => handelConfirm()}
          disabled={checkCount === 0}
        >
          선택 완료
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InspectionGroupModal;
